import React from 'react';
import {
  startOfMonth,
  startOfWeek,
  endOfMonth,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isWeekend,
  differenceInCalendarWeeks
} from 'date-fns';

import {
  isEndOfMonth,
  isStartOfMonth,
  localizedFullMonthName,
  localizedDayOfWeekShort,
  isEndOfWeek
} from '@card/commonlib';
import {
  Weeks,
  DayName,
  Day,
  DayHeader,
  DayContextualInfo,
  DayNumber,
  WeekTotal,
  WeekName
} from './CalendarStyles';

export interface RenderDayContext {
  day: Date;
  isToday: boolean;
  isCurrentMonth: boolean;
}

export interface RenderWeeklySummaryContext {
  day: Date;
  weekOfMonthNumber: number;
}

export interface CalendarProps {
  month: number;
  year: number;
  renderDay: (context: RenderDayContext) => React.ReactNode;
  renderWeeklySummary: (context: RenderWeeklySummaryContext) => React.ReactNode;
}

export function Calendar({ month, year, renderDay, renderWeeklySummary }: CalendarProps) {
  const referenceDate = new Date(year, month);
  const today = new Date();
  const fromDay = startOfWeek(startOfMonth(referenceDate));
  const toDay = endOfWeek(endOfMonth(referenceDate));
  const days = eachDayOfInterval({ start: fromDay, end: toDay });
  const firstWeek = days.slice(0, 7);
  const weekOfMonthNumber = (date: Date) => differenceInCalendarWeeks(date, referenceDate) + 1;

  return (
    <Weeks>
      {firstWeek.map((day) => (
        <DayName key={day.valueOf()}>{localizedDayOfWeekShort(day)}</DayName>
      ))}
      <DayName style={{ textAlign: 'right' }}>Total</DayName>
      {days.map((day) => {
        const isToday = isSameDay(day, today);
        const isCurrentMonth = isSameMonth(day, referenceDate);
        return (
          <React.Fragment key={day.valueOf()}>
            <Day isCurrentMonth={isCurrentMonth} isToday={isToday} isWeekend={isWeekend(day)}>
              <DayHeader>
                <DayContextualInfo>
                  {isToday && 'Hoy'}
                  {!isCurrentMonth &&
                    (isStartOfMonth(day) || isEndOfMonth(day)) &&
                    localizedFullMonthName(day)}
                </DayContextualInfo>
                <DayNumber isToday={isToday}>{day.getDate()}</DayNumber>
              </DayHeader>
              {renderDay({ day, isToday, isCurrentMonth })}
            </Day>
            {isEndOfWeek(day) && (
              <WeekTotal>
                <WeekName>Semana {weekOfMonthNumber(day)}</WeekName>
                {renderWeeklySummary({ day, weekOfMonthNumber: weekOfMonthNumber(day) })}
              </WeekTotal>
            )}
          </React.Fragment>
        );
      })}
    </Weeks>
  );
}
